import React, { useEffect, useState } from "react";
import { Sparkles, PenLine, Ban } from "lucide-react";
import api from "@/lib/api";

export const AiFeatureMap = ({ testId = "ai-feature-map" }) => {
  const [map, setMap] = useState(null);

  useEffect(() => {
    api.get("/ai/feature-map").then(({ data }) => setMap(data)).catch(() => {});
  }, []);

  if (!map) return null;

  return (
    <section className="panel p-5" data-testid={testId}>
      <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
        <Sparkles size={15} className="text-slate-400" /> What AI actually touches
      </h3>
      <p className="mt-1 max-w-4xl text-xs text-slate-600" data-testid="ai-map-headline">
        {map.headline}{" "}
        {map.ai_enabled
          ? "AI is currently active."
          : "AI is currently switched off or locked, so the features below are in manual mode."}
      </p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 font-semibold">Feature</th>
              <th className="px-3 py-2 font-semibold">What AI does</th>
              <th className="px-3 py-2 font-semibold">With AI switched off</th>
              <th className="px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {map.ai_features.map((f) => (
              <tr key={f.key} data-testid={`ai-feature-${f.key}`}>
                <td className="px-3 py-3 align-top">
                  <p className="font-semibold text-slate-800">{f.name}</p>
                  <p className="text-xs text-slate-500">{f.where}</p>
                </td>
                <td className="px-3 py-3 align-top text-xs text-slate-600">{f.ai_does}</td>
                <td className="px-3 py-3 align-top text-xs text-slate-600">{f.without_ai}</td>
                <td className="px-3 py-3 align-top">
                  {f.status === "available" ? (
                    <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                      <Sparkles size={10} /> AI active
                    </span>
                  ) : f.status === "manual_only" ? (
                    <span className="inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-700">
                      <PenLine size={10} /> Manual
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700">
                      <Ban size={10} /> Unavailable
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 border-t border-slate-100 pt-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          No AI involvement at all — unaffected whatever you choose ({map.counts.non_ai} areas)
        </h4>
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3" data-testid="ai-map-non-ai">
          {map.non_ai_features.map((f) => (
            <div key={f.name} className="rounded border border-slate-100 bg-slate-50 p-2.5">
              <p className="text-xs font-semibold text-slate-700">{f.name}</p>
              <p className="text-[11px] text-slate-500">{f.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
